import { FormEvent, useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, LockKeyhole } from "lucide-react";
import { authCallbackSnapshot, supabase, validatedAuthCallbackSession } from "../lib/supabase";

type Mode = "invitation" | "reset";
type LinkState = "checking" | "ready" | "missing" | "unreadable" | "expired" | "used" | "success";

function copy(mode: Mode) {
  return mode === "invitation"
    ? {
      title: "Définir votre mot de passe",
      intro: "Bienvenue dans votre espace partenaire. Choisissez un mot de passe d’au moins 10 caractères pour activer votre accès.",
      button: "Activer mon accès",
      missing: "Lien d’invitation introuvable",
    }
    : {
      title: "Créer un nouveau mot de passe",
      intro: "Choisissez un nouveau mot de passe d’au moins 10 caractères.",
      button: "Enregistrer le mot de passe",
      missing: "Lien de réinitialisation introuvable",
    };
}

export default function PasswordDefinitionExperience({ mode }: { mode: Mode }) {
  const [state, setState] = useState<LinkState>("checking");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const text = copy(mode);

  useEffect(() => {
    let active = true;
    let settled = false;
    const expectedType = mode === "invitation" ? "invite" : "recovery";
    const callbackMatchesPage = authCallbackSnapshot.path === window.location.pathname;
    const settle = (next: LinkState) => {
      if (!active || settled) return;
      settled = true;
      setState(next);
    };
    if (!callbackMatchesPage) {
      settle("missing");
      return;
    }
    if (authCallbackSnapshot.error) {
      settle(authCallbackSnapshot.error);
      return;
    }
    if (!authCallbackSnapshot.hasImplicitTokens) {
      settle(authCallbackSnapshot.type ? "unreadable" : "missing");
      return;
    }
    if (authCallbackSnapshot.type !== expectedType || !authCallbackSnapshot.userId) {
      settle("unreadable");
      return;
    }
    validatedAuthCallbackSession.then(session => {
      if (session?.user.id === authCallbackSnapshot.userId) settle("ready");
    });
    const timeout = window.setTimeout(() => settle("unreadable"), 3500);
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [mode]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if (password.length < 10) return setError("Votre mot de passe doit contenir au moins 10 caractères.");
    if (password !== confirmation) return setError("Les deux mots de passe ne correspondent pas.");
    setSaving(true);
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || sessionData.session?.user.id !== authCallbackSnapshot.userId) {
      setSaving(false);
      setState("unreadable");
      return;
    }
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (updateError) {
      setState("unreadable");
      return;
    }
    setState("success");
    window.setTimeout(() => window.location.replace("/"), 1400);
  };

  const unavailable = ["missing", "unreadable", "expired", "used"].includes(state);
  return (
    <main className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/20"><LockKeyhole className="h-6 w-6" /></div>
          <h1 className="text-2xl font-semibold text-foreground">Espace Partenaire Jobotto</h1>
          <p className="mt-2 text-sm text-muted-foreground">Un accès clair, sécurisé, réservé à votre équipe.</p>
        </div>
        <section className="rounded-2xl border border-border bg-card p-6 shadow-xl shadow-primary/5">
          {state === "checking" && <div className="flex flex-col items-center gap-3 py-8 text-sm text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-primary" />Vérification du lien sécurisé…</div>}
          {unavailable && <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl border border-destructive/20 bg-destructive/10 p-4"><AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" /><div>
              <h2 className="font-medium">{state === "used" ? "Ce lien a déjà été utilisé" : state === "expired" ? "Ce lien a expiré" : state === "unreadable" ? "Ce lien est illisible" : text.missing}</h2>
              <p className="mt-1 text-sm text-muted-foreground">Demandez un nouveau lien à l’administrateur de votre société ou depuis la page de connexion.</p>
            </div></div>
          </div>}
          {state === "ready" && <form onSubmit={submit} className="space-y-5">
            <div><h2 className="text-lg font-semibold">{text.title}</h2><p className="mt-1 text-sm text-muted-foreground">{text.intro}</p></div>
            {error && <p className="rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
            <label className="block text-sm font-medium">Nouveau mot de passe<input autoFocus required minLength={10} type="password" value={password} onChange={e => setPassword(e.target.value)} className="mt-1.5 h-11 w-full rounded-lg border border-input bg-background px-3 focus:outline-none focus:ring-2 focus:ring-ring" /></label>
            <label className="block text-sm font-medium">Confirmer le mot de passe<input required minLength={10} type="password" value={confirmation} onChange={e => setConfirmation(e.target.value)} className="mt-1.5 h-11 w-full rounded-lg border border-input bg-background px-3 focus:outline-none focus:ring-2 focus:ring-ring" /></label>
            <button disabled={saving} className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />}{text.button}</button>
          </form>}
          {state === "success" && <div className="space-y-3 py-4 text-center"><CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" /><h2 className="text-lg font-semibold">Mot de passe mis à jour</h2><p className="text-sm text-muted-foreground">Votre espace partenaire va s’ouvrir automatiquement.</p></div>}
        </section>
      </div>
    </main>
  );
}